import React, { useEffect, useState, useRef } from "react";
import { gettrialbalance, listBranches } from "../../../../services/admin";
import { toast } from "react-toastify";
import moment from "moment";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { printElement } from "../../../../utils/printTable";

export default function TrialBalance() {
    const [asOnDate, setAsOnDate] = useState(moment().format("YYYY-MM-DD"));
    const [trialBalance, setTrialBalance] = useState([]);
    const [totals, setTotals] = useState({ debit: 0, credit: 0, difference: 0 });
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(false);
    const printRef = useRef();

    useEffect(() => {
        // Fetch branches for display purposes
        listBranches().then((res) => {
            if (res.status === true) {
                setBranches(res.data);
            }
        });

        // Auto-fetch trial balance on mount
        fetchTrialBalance();
    }, []);

    const fetchTrialBalance = () => {
        // Get branch from localStorage
        const userBranch = localStorage.getItem("branch");

        if (!userBranch) {
            toast.error("Branch information not found");
            return;
        }

        setLoading(true);
        const data = {
            as_on_date: asOnDate,
            branch_id: userBranch, // Try changing this to 4 temporarily for testing: branch_id: 4,
        };

        gettrialbalance(data).then((res) => {
            if (res.status === true) {
                setTrialBalance(res.data.accounts || []);
                setTotals({
                    debit: res.data.total_debit || 0,
                    credit: res.data.total_credit || 0,
                    difference: res.data.difference || 0,
                });
            } else {
                toast.error(res.message || 'Failed to fetch trial balance');
            }
            setLoading(false);
        }).catch((error) => {
            console.error('Trial Balance API Error:', error);
            toast.error('Failed to load trial balance data');
            setLoading(false);
        });
    };

    const handleGenerate = () => {
        fetchTrialBalance();
    };

    const handlePdfExport = async (mode = "pdf") => {
        const input = printRef.current;

        if (mode === "print") {
            // Print the sheet as markup - a browser cannot be relied on to
            // print a PDF held in a frame.
            const userBranch = localStorage.getItem("branch");
            const branchName = branches.find(b => b.id == userBranch)?.branch_name || 'All';
            printElement(input, {
                title: `Trial Balance - ${branchName} - ${moment(asOnDate).format("DD-MMM-YYYY")}`
            });
            return;
        }

        const canvas = await html2canvas(input, { scale: 2, useCORS: true });
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");

        const imgWidth = 210;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        // Get branch from localStorage
        const userBranch = localStorage.getItem("branch");
        const branchName = branches.find(b => b.id == userBranch)?.branch_name || 'All';
        pdf.text(`Trial Balance - ${branchName} - ${moment(asOnDate).format("DD-MMM-YYYY")}`, 14, 15);
        pdf.addImage(imgData, "PNG", 0, 20, imgWidth, imgHeight);

        pdf.save(`Trial_Balance_${branchName}_${asOnDate}.pdf`);
    };

    const handleExcelExport = () => {
        // Get branch from localStorage
        const userBranch = localStorage.getItem("branch");
        const branchName = branches.find(b => b.id == userBranch)?.branch_name || 'All';

        let csvContent = "data:text/csv;charset=utf-8,";
        csvContent += `Trial Balance - ${branchName} - ${moment(asOnDate).format("DD-MMM-YYYY")}\n\n`;
        csvContent += "Account Code,Account Name,Account Type,Debit,Credit\n";

        trialBalance.forEach((account) => {
            const debit = account.debit > 0 ? parseFloat(account.debit).toFixed(2) : "";
            const credit = account.credit > 0 ? parseFloat(account.credit).toFixed(2) : "";
            const name = `"${account.account_name.replace(/"/g, '""')}"`;
            csvContent += `${account.account_code || ""},${name},${account.account_type || ""},${debit},${credit}\n`;
        });

        csvContent += `Total,,,"${parseFloat(totals.debit).toFixed(2)}","${parseFloat(totals.credit).toFixed(2)}"\n`;

        if (totals.difference > 0) {
            csvContent += `Difference,,,,"${parseFloat(totals.difference).toFixed(2)}"\n`;
        }

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `Trial_Balance_${branchName}_${asOnDate}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div>
            <div className="page-header">
                <h2 className="main-content-title tx-24 mg-b-5">Trial Balance</h2>
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <a>Accounts</a>
                    </li>
                    <li className="breadcrumb-item">
                        <a>Reports</a>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                        Trial Balance
                    </li>
                </ol>
            </div>

            {loading && (
                <div className="loader-wrapper">
                    <div className="loader"></div>
                    <h3>Loading...Please do not go back or close the window.</h3>
                </div>
            )}

            {!loading && (
            <div className="content-body">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-header">
                                    <div className="row">
                                        <div className="col-md-6">
                                            <h4 className="card-title">Trial Balance</h4>
                                        </div>
                                        <div className="col-md-6 text-end">
                                            <button className="btn btn-danger me-2" onClick={() => handlePdfExport("pdf")}>
                                                <i className="fa fa-file-pdf"></i> Download PDF
                                            </button>
                                            <button className="btn btn-success me-2" onClick={handleExcelExport}>
                                                <i className="fa fa-file-excel"></i> Download Excel
                                            </button>
                                            <button className="btn btn-primary" onClick={() => handlePdfExport("print")}>
                                                <i className="fa fa-print"></i> Print
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body">
                            <div className="row mb-4">
                                <div className="col-md-4">
                                    <label className="form-label">As On Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={asOnDate}
                                        onChange={(e) => setAsOnDate(e.target.value)}
                                    />
                                </div>
                                <div className="col-md-2 d-flex align-items-end">
                                    <button className="btn btn-primary" onClick={handleGenerate}>
                                        <i className="fa fa-search"></i> Generate
                                    </button>
                                </div>
                            </div>

                            <div className="table-responsive" ref={printRef}>
                                <table className="table table-bordered">
                                    <thead className="table-light">
                                        <tr>
                                            <th width="15%">Account Code</th>
                                            <th width="40%">Account Name</th>
                                            <th width="15%">Account Type</th>
                                            <th width="15%" className="text-end">Debit (₹)</th>
                                            <th width="15%" className="text-end">Credit (₹)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {trialBalance.length > 0 ? (
                                            trialBalance.map((account, index) => (
                                                <tr key={index}>
                                                    <td>{account.account_code}</td>
                                                    <td>{account.account_name}</td>
                                                    <td>{account.account_type}</td>
                                                    <td className="text-end">
                                                        {account.debit > 0
                                                            ? parseFloat(account.debit).toFixed(2)
                                                            : "-"}
                                                    </td>
                                                    <td className="text-end">
                                                        {account.credit > 0
                                                            ? parseFloat(account.credit).toFixed(2)
                                                            : "-"}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="5" className="text-center">
                                                    No data available
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                    <tfoot className="table-secondary">
                                        <tr>
                                            <th colSpan="3" className="text-end">
                                                Total
                                            </th>
                                            <th className="text-end">
                                                {parseFloat(totals.debit).toFixed(2)}
                                            </th>
                                            <th className="text-end">
                                                {parseFloat(totals.credit).toFixed(2)}
                                            </th>
                                        </tr>
                                        {totals.difference > 0 && (
                                            <tr>
                                                <th colSpan="3" className="text-end text-danger">
                                                    Difference
                                                </th>
                                                <th colSpan="2" className="text-end text-danger">
                                                    {parseFloat(totals.difference).toFixed(2)}
                                                </th>
                                            </tr>
                                        )}
                                    </tfoot>
                                </table>
                            </div>

                            {totals.difference === 0 && trialBalance.length > 0 && (
                                <div className="alert alert-success mt-3">
                                    <i className="fa fa-check-circle"></i> Trial Balance is balanced!
                                </div>
                            )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            )}
        </div>
    );
}
